import { selectFiles } from '@app/components/FileUpload/modules/fileUpload'
import { Address } from '@app/models/Address'
import { Area } from '@app/models/Area'
import { AssociatedContact } from '@app/models/AssociatedContact'
import { Attachment } from '@app/models/Attachment'
import { Comp, IComp } from '@app/models/Comp'
import { DevelopmentSite } from '@app/models/DevelopmentSite'
import { PropertyType } from '@app/models/enums'
import { FileUploadAttachment } from '@app/models/FileUploadAttachment'
import { ILot, Lot } from '@app/models/Lot'
import { IProperty, Property } from '@app/models/Property'
import { ISign, Sign } from '@app/models/Sign'
import { ISocialConnection } from '@app/models/SocialConnection'
import { ISpace, Space } from '@app/models/Space'
import {
  ITransactionHistory,
  TransactionHistory,
} from '@app/models/TransactionHistory'
import { ApplicationState } from '@app/store'
import { errorToast, successToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest, RequestMethod } from '@app/utils/request'
import { List, Map, Record } from 'immutable'
import { push } from 'connected-react-router'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

export const ATTACHMENTS_IDENTIFIER = 'property-attachment-editor'
export const PHOTOS_IDENTIFIER = 'property-photo-editor'

// -------------------------
// STATE
// -------------------------

export interface IPropertyEditState {
  readonly canSave: boolean
  readonly error?: string
  readonly isLoading: boolean
  readonly isReady: boolean
  readonly isSaving: boolean
  readonly property: IProperty
}

const StateRecord = Record({
  canSave: false,
  error: undefined,
  isLoading: false,
  isReady: false,
  isSaving: false,
  property: undefined,
})

export class PropertyEditState extends StateRecord
  implements IPropertyEditState {
  public static fromJS(initialState?: Partial<IPropertyEditState>) {
    return new PropertyEditState(initialState)
  }

  public readonly canSave: boolean
  public readonly error?: string
  public readonly isLoading: boolean
  public readonly isReady: boolean
  public readonly isSaving: boolean
  public readonly property: Property

  constructor(props?: Partial<IPropertyEditState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum ActionTypes {
  AddComp = 'PROPERTY_EDIT::ADD_COMP',
  AddLot = 'PROPERTY_EDIT::ADD_LOT',
  AddSpace = 'PROPERTY_EDIT::ADD_SPACE',
  AddTransaction = 'PROPERTY_EDIT:ADD_TRANSACTION',
  EditLot = 'PROPERTY_EDIT::EDIT_LOT',
  EditSpace = 'PROPERTY_EDIT::EDIT_SPACE',
  LoadAddProperty = 'PROPERTY_EDIT::LOAD_ADD_PROPERTY',
  LoadEditProperty = 'PROPERTY_EDIT::LOAD_EDIT_PROPERTY',
  LoadPropertyFailure = 'PROPERTY_EDIT::LOAD_PROPERTY_FAILURE',
  LoadPropertyRequest = 'PROPERTY_EDIT::LOAD_PROPERTY_REQUEST',
  LoadPropertySuccess = 'PROPERTY_EDIT::LOAD_PROPERTY_SUCCESS',
  RemoveComp = 'PROPERTY_EDIT::REMOVE_COMP',
  RemoveLot = 'PROPERTY_EDIT::REMOVE_LOT',
  RemoveSpace = 'PROPERTY_EDIT::REMOVE_SPACE',
  RemoveTransaction = 'PROPERTY_EDIT::REMOVE_TRANSACTION',
  ResetState = 'PROPERTY_EDIT::RESET_STATE',
  SaveProperty = 'PROPERTY_EDIT::SAVE_PROPERTY',
  SavePropertyFailure = 'PROPERTY_EDIT::SAVE_PROPERTY_FAILURE',
  SavePropertyRequest = 'PROPERTY_EDIT::SAVE_PROPERTY_REQUEST',
  SavePropertySuccess = 'PROPERTY_EDIT::SAVE_PROPERTY_SUCCESS',
  SetAddress = 'PROPERTY_EDIT::SET_ADDRESS',
  SetAvgDailyRate = 'PROPERTY_EDIT::SET_AVG_DAILY_RATE',
  SetAvgOccupancy = 'PROPERTY_EDIT::SET_AVG_OCCUPANCY',
  SetBuildingClass = 'PROPERTY_EDIT::SET_BUILDING_CLASS',
  SetBuildingSize = 'PROPERTY_EDIT::SET_BUILDING_SIZE',
  SetCeilingHeight = 'PROPERTY_EDIT::SET_CEILING_HEIGHT',
  SetCorridor = 'PROPERTY_EDIT::SET_CORRIDOR',
  SetDockHighDoors = 'PROPERTY_EDIT::SET_DOCK_HIGH_DOORS',
  SetDriveInDoors = 'PROPERTY_EDIT::SET_DRIVE_IN_DOORS',
  SetGrossLeasableArea = 'PROPERTY_EDIT::SET_GROSS_LEASABLE_AREA',
  SetGrossRentMultiplier = 'PROPERTY_EDIT::SET_GROSS_RENT_MULTIPLIER',
  SetHighlights = 'PROPERTY_EDIT::SET_HIGHLIGHTS',
  SetInsuranceExpenses = 'PROPERTY_EDIT::SET_INSURANCE_EXPENSES',
  SetLatitude = 'PROPERTY_EDIT::SET_LATITUDE',
  SetLongitude = 'PROPERTY_EDIT::SET_LONGITUDE',
  SetLocationDescription = 'PROPERTY_EDIT::SET_LOCATION_DESCRIPTION',
  SetLotSize = 'PROPERTY_EDIT::SET_LOT_SIZE',
  SetMainPhoto = 'PROPERTY_EDIT::SET_MAIN_PHOTO',
  SetNumberOfUnits = 'PROPERTY_EDIT::SET_NUMBER_OF_UNITS',
  SetOccupancy = 'PROPERTY_EDIT::SET_OCCUPANCY',
  SetOtherExpenses = 'PROPERTY_EDIT::SET_OTHER_EXPENSES',
  SetOwners = 'PROPERTY_EDIT::SET_OWNERS',
  SetParcelId = 'PROPERTY_EDIT::SET_PARCEL_ID',
  SetPropertyDescription = 'PROPERTY_EDIT::SET_PROPERTY_DESCRIPTION',
  SetPropertyName = 'PROPERTY_EDIT::SET_PROPERTY_NAME',
  SetPropertyTypes = 'PROPERTY_EDIT::SET_PROPERTY_TYPES',
  SetRealEstateTaxes = 'PROPERTY_EDIT::SET_REAL_ESTATE_TAXES',
  SetRelatedConnections = 'PROPERTY_EDIT::SET_RELATED_CONNECTIONS',
  SetRevPerPAR = 'PROPERTY_EDIT::SET_REV_PER_PAR',
  SetSigns = 'PROPERTY_EDIT::SET_SIGNS',
  SetChatChannelId = 'PROPERTY_EDIT::SET_CHAT_CHANNEL_ID',
  SetSocial = 'PROPERTY_EDIT::SET_SOCIAL',
  SetYearBuilt = 'PROPERTY_EDIT::SET_YEAR_BUILT',
  SetZoningDescription = 'PROPERTY_EDIT::SET_ZONING_DESCRIPTION',
}

interface IAddCompAction {
  type: ActionTypes.AddComp
  value: IComp
}

interface IAddLotAction {
  type: ActionTypes.AddLot
  value: ILot
}

interface IAddSpaceAction {
  type: ActionTypes.AddSpace
  value: ISpace
}

interface IAddTransactionAction {
  mode: string
  type: ActionTypes.AddTransaction
  value: ITransactionHistory
}

interface IEditLotAction {
  type: ActionTypes.EditLot
  value: ILot
}

interface IEditSpaceAction {
  type: ActionTypes.EditSpace
  value: ISpace
}

interface ILoadAddPropertyAction {
  siteId?: string
  type: ActionTypes.LoadAddProperty
}

interface ILoadEditPropertyAction {
  propertyId: string
  type: ActionTypes.LoadEditProperty
}

interface ILoadPropertyFailureAction {
  error: string
  type: ActionTypes.LoadPropertyFailure
}

interface ILoadPropertyRequestAction {
  type: ActionTypes.LoadPropertyRequest
}

interface ILoadPropertySuccessAction {
  type: ActionTypes.LoadPropertySuccess
  value: Property
}

interface IRemoveCompAction {
  type: ActionTypes.RemoveComp
  value: IComp
}

interface IRemoveLotAction {
  type: ActionTypes.RemoveLot
  value: ILot
}

interface IRemoveSpaceAction {
  type: ActionTypes.RemoveSpace
  value: ISpace
}

interface IRemoveTransactionAction {
  type: ActionTypes.RemoveTransaction
  value: ITransactionHistory
}

interface IResetStateAction {
  type: ActionTypes.ResetState
}

interface ISavePropertyAction {
  property: Property
  type: ActionTypes.SaveProperty
}

interface ISavePropertyFailureAction {
  error: string
  type: ActionTypes.SavePropertyFailure
}

interface ISavePropertyRequestAction {
  type: ActionTypes.SavePropertyRequest
}

interface ISavePropertySuccessAction {
  type: ActionTypes.SavePropertySuccess
}

interface ISetAddressAction {
  type: ActionTypes.SetAddress
  value: Address
}

interface ISetAvgDailyRateAction {
  type: ActionTypes.SetAvgDailyRate
  value: string
}

interface ISetAvgOccupancyAction {
  type: ActionTypes.SetAvgOccupancy
  value: string
}

interface ISetBuildingClassAction {
  type: ActionTypes.SetBuildingClass
  value: string
}

interface ISetBuildingSizeAction {
  type: ActionTypes.SetBuildingSize
  value: string
}

interface ISetCeilingHeightAction {
  type: ActionTypes.SetCeilingHeight
  value: string
}

interface ISetCorridorAction {
  type: ActionTypes.SetCorridor
  value: string
}

interface ISetDockHighDoorsAction {
  type: ActionTypes.SetDockHighDoors
  value: string
}

interface ISetDriveInDoorsAction {
  type: ActionTypes.SetDriveInDoors
  value: string
}

interface ISetGrossLeasableAreaAction {
  type: ActionTypes.SetGrossLeasableArea
  value: string
}

interface ISetGrossRentMultiplierAction {
  type: ActionTypes.SetGrossRentMultiplier
  value: string
}

interface ISetHighlightsAction {
  type: ActionTypes.SetHighlights
  value: List<string>
}

interface ISetInsuranceExpensesAction {
  type: ActionTypes.SetInsuranceExpenses
  value: string
}

interface ISetLatitudeAction {
  type: ActionTypes.SetLatitude
  value: number
}

interface ISetLongitudeAction {
  type: ActionTypes.SetLongitude
  value: number
}

interface ISetLocationDescriptionAction {
  type: ActionTypes.SetLocationDescription
  value: string
}

interface ISetLotSizeAction {
  type: ActionTypes.SetLotSize
  value: Area
}

interface ISetMainPhotoAction {
  photoId?: string
  type: ActionTypes.SetMainPhoto
}

interface ISetNumberOfUnitsAction {
  type: ActionTypes.SetNumberOfUnits
  value: string
}

interface ISetOccupancyAction {
  type: ActionTypes.SetOccupancy
  value: string
}

interface ISetOtherExpensesAction {
  type: ActionTypes.SetOtherExpenses
  value: string
}

interface ISetOwnersAction {
  type: ActionTypes.SetOwners
  value: List<AssociatedContact>
}

interface ISetParcelIdAction {
  type: ActionTypes.SetParcelId
  value: string
}

interface ISetPropertyDescriptionAction {
  type: ActionTypes.SetPropertyDescription
  value: string
}

interface ISetPropertyNameAction {
  type: ActionTypes.SetPropertyName
  value: string
}

interface ISetPropertyTypesAction {
  type: ActionTypes.SetPropertyTypes
  value: List<PropertyType>
}

interface ISetRealEstateTaxesAction {
  type: ActionTypes.SetRealEstateTaxes
  value: string
}

interface ISetRelatedConnectionsAction {
  type: ActionTypes.SetRelatedConnections
  value: List<AssociatedContact>
}

interface ISetRevPerParAction {
  type: ActionTypes.SetRevPerPAR
  value: string
}

interface ISetSignsAction {
  type: ActionTypes.SetSigns
  value: List<ISign>
}

interface ISetChatChannelIdAction {
  type: ActionTypes.SetChatChannelId
  value: string
}

interface ISetSocialAction {
  type: ActionTypes.SetSocial
  value: ISocialConnection
}

interface ISetYearBuiltAction {
  type: ActionTypes.SetYearBuilt
  value: string
}

interface ISetZoningDescriptionAction {
  type: ActionTypes.SetZoningDescription
  value: string
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | IAddCompAction
  | IAddLotAction
  | IAddSpaceAction
  | IAddTransactionAction
  | IEditLotAction
  | IEditSpaceAction
  | ILoadPropertyFailureAction
  | ILoadPropertyRequestAction
  | ILoadPropertySuccessAction
  | IRemoveCompAction
  | IRemoveLotAction
  | IRemoveSpaceAction
  | IRemoveTransactionAction
  | IResetStateAction
  | ISavePropertyFailureAction
  | ISavePropertyRequestAction
  | ISavePropertySuccessAction
  | ISetAddressAction
  | ISetAvgDailyRateAction
  | ISetAvgOccupancyAction
  | ISetBuildingClassAction
  | ISetBuildingSizeAction
  | ISetCeilingHeightAction
  | ISetCorridorAction
  | ISetDockHighDoorsAction
  | ISetDriveInDoorsAction
  | ISetGrossLeasableAreaAction
  | ISetGrossRentMultiplierAction
  | ISetHighlightsAction
  | ISetInsuranceExpensesAction
  | ISetLatitudeAction
  | ISetLongitudeAction
  | ISetLocationDescriptionAction
  | ISetLotSizeAction
  | ISetMainPhotoAction
  | ISetNumberOfUnitsAction
  | ISetOccupancyAction
  | ISetOtherExpensesAction
  | ISetOwnersAction
  | ISetParcelIdAction
  | ISetPropertyDescriptionAction
  | ISetPropertyNameAction
  | ISetPropertyTypesAction
  | ISetRealEstateTaxesAction
  | ISetRelatedConnectionsAction
  | ISetRevPerParAction
  | ISetSignsAction
  | ISetChatChannelIdAction
  | ISetSocialAction
  | ISetYearBuiltAction
  | ISetZoningDescriptionAction

// -------------------------
// ACTION CREATORS
// -------------------------

function addComp(value: IComp): IAddCompAction {
  return {
    type: ActionTypes.AddComp,
    value,
  }
}

function addLot(value: ILot): IAddLotAction {
  return {
    type: ActionTypes.AddLot,
    value,
  }
}

function addSpace(value: ISpace): IAddSpaceAction {
  return {
    type: ActionTypes.AddSpace,
    value,
  }
}

function addTransaction(
  value: ITransactionHistory,
  mode: string,
): IAddTransactionAction {
  return {
    mode,
    type: ActionTypes.AddTransaction,
    value,
  }
}

function editLot(value: ILot): IEditLotAction {
  return {
    type: ActionTypes.EditLot,
    value,
  }
}

function editSpace(value: ISpace): IEditSpaceAction {
  return {
    type: ActionTypes.EditSpace,
    value,
  }
}

function loadAddProperty(siteId?: string): ILoadAddPropertyAction {
  return {
    siteId,
    type: ActionTypes.LoadAddProperty,
  }
}

function loadEditProperty(propertyId: string): ILoadEditPropertyAction {
  return {
    propertyId,
    type: ActionTypes.LoadEditProperty,
  }
}

function loadPropertyFailure(error: string): ILoadPropertyFailureAction {
  return {
    error,
    type: ActionTypes.LoadPropertyFailure,
  }
}

function loadPropertyRequest(): ILoadPropertyRequestAction {
  return { type: ActionTypes.LoadPropertyRequest }
}

function loadPropertySuccess(value: Property): ILoadPropertySuccessAction {
  return {
    type: ActionTypes.LoadPropertySuccess,
    value,
  }
}

function removeComp(value: IComp): IRemoveCompAction {
  return {
    type: ActionTypes.RemoveComp,
    value,
  }
}

function removeLot(value: ILot): IRemoveLotAction {
  return {
    type: ActionTypes.RemoveLot,
    value,
  }
}

function removeSpace(value: ISpace): IRemoveSpaceAction {
  return {
    type: ActionTypes.RemoveSpace,
    value,
  }
}

function removeTransaction(
  value: ITransactionHistory,
): IRemoveTransactionAction {
  return {
    type: ActionTypes.RemoveTransaction,
    value,
  }
}

function resetState(): IResetStateAction {
  return { type: ActionTypes.ResetState }
}

function save(property: Property): ISavePropertyAction {
  return {
    property,
    type: ActionTypes.SaveProperty,
  }
}

function saveFailure(error: string): ISavePropertyFailureAction {
  return {
    error,
    type: ActionTypes.SavePropertyFailure,
  }
}

function saveRequest(): ISavePropertyRequestAction {
  return { type: ActionTypes.SavePropertyRequest }
}

function saveSuccess(): ISavePropertySuccessAction {
  return { type: ActionTypes.SavePropertySuccess }
}

function setAddress(value: Address): ISetAddressAction {
  return {
    type: ActionTypes.SetAddress,
    value,
  }
}

function setAvgDailyRate(value: string): ISetAvgDailyRateAction {
  return {
    type: ActionTypes.SetAvgDailyRate,
    value,
  }
}

function setAvgOccupancy(value: string): ISetAvgOccupancyAction {
  return {
    type: ActionTypes.SetAvgOccupancy,
    value,
  }
}

function setBuildingClass(value: string): ISetBuildingClassAction {
  return {
    type: ActionTypes.SetBuildingClass,
    value,
  }
}

function setBuildingSize(value: string): ISetBuildingSizeAction {
  return {
    type: ActionTypes.SetBuildingSize,
    value,
  }
}

function setCeilingHeight(value: string): ISetCeilingHeightAction {
  return {
    type: ActionTypes.SetCeilingHeight,
    value,
  }
}

function setCorridor(value: string): ISetCorridorAction {
  return {
    type: ActionTypes.SetCorridor,
    value,
  }
}

function setDockHighDoors(value: string): ISetDockHighDoorsAction {
  return {
    type: ActionTypes.SetDockHighDoors,
    value,
  }
}

function setDriveInDoors(value: string): ISetDriveInDoorsAction {
  return {
    type: ActionTypes.SetDriveInDoors,
    value,
  }
}

function setGrossLeasableArea(value: string): ISetGrossLeasableAreaAction {
  return {
    type: ActionTypes.SetGrossLeasableArea,
    value,
  }
}

function setGrossRentMultiplier(value: string): ISetGrossRentMultiplierAction {
  return {
    type: ActionTypes.SetGrossRentMultiplier,
    value,
  }
}

function setHighlights(value: List<string>): ISetHighlightsAction {
  return {
    type: ActionTypes.SetHighlights,
    value,
  }
}

function setInsuranceExpenses(value: string): ISetInsuranceExpensesAction {
  return {
    type: ActionTypes.SetInsuranceExpenses,
    value,
  }
}

function setLatitude(value: number): ISetLatitudeAction {
  return {
    type: ActionTypes.SetLatitude,
    value,
  }
}

function setLongitude(value: number): ISetLongitudeAction {
  return {
    type: ActionTypes.SetLongitude,
    value,
  }
}

function setLocationDescription(value: string): ISetLocationDescriptionAction {
  return {
    type: ActionTypes.SetLocationDescription,
    value,
  }
}

function setLotSize(value: Area): ISetLotSizeAction {
  return {
    type: ActionTypes.SetLotSize,
    value,
  }
}

function setMainPhoto(photoId?: string): ISetMainPhotoAction {
  return {
    photoId,
    type: ActionTypes.SetMainPhoto,
  }
}

function setNumberOfUnits(value: string): ISetNumberOfUnitsAction {
  return {
    type: ActionTypes.SetNumberOfUnits,
    value,
  }
}

function setOccupancy(value: string): ISetOccupancyAction {
  return {
    type: ActionTypes.SetOccupancy,
    value,
  }
}

function setOtherExpenses(value: string): ISetOtherExpensesAction {
  return {
    type: ActionTypes.SetOtherExpenses,
    value,
  }
}

function setOwners(value: List<AssociatedContact>): ISetOwnersAction {
  return {
    type: ActionTypes.SetOwners,
    value,
  }
}

function setParcelId(value: string): ISetParcelIdAction {
  return {
    type: ActionTypes.SetParcelId,
    value,
  }
}

function setPropertyDescription(value: string): ISetPropertyDescriptionAction {
  return {
    type: ActionTypes.SetPropertyDescription,
    value,
  }
}

function setPropertyName(value: string): ISetPropertyNameAction {
  return {
    type: ActionTypes.SetPropertyName,
    value,
  }
}

function setPropertyTypes(value: List<PropertyType>): ISetPropertyTypesAction {
  return {
    type: ActionTypes.SetPropertyTypes,
    value,
  }
}

function setRealEstateTaxes(value: string): ISetRealEstateTaxesAction {
  return {
    type: ActionTypes.SetRealEstateTaxes,
    value,
  }
}

function setRelatedConnections(
  value: List<AssociatedContact>,
): ISetRelatedConnectionsAction {
  return {
    type: ActionTypes.SetRelatedConnections,
    value,
  }
}

function setRevPerPAR(value: string): ISetRevPerParAction {
  return {
    type: ActionTypes.SetRevPerPAR,
    value,
  }
}

function setSigns(value: List<ISign>): ISetSignsAction {
  return {
    type: ActionTypes.SetSigns,
    value,
  }
}

function setChatChannelId(value: string): ISetChatChannelIdAction {
  return {
    type: ActionTypes.SetChatChannelId,
    value,
  }
}

function setSocial(value: ISocialConnection): ISetSocialAction {
  return {
    type: ActionTypes.SetSocial,
    value,
  }
}

function setYearBuilt(value: string): ISetYearBuiltAction {
  return {
    type: ActionTypes.SetYearBuilt,
    value,
  }
}

function setZoningDescription(value: string): ISetZoningDescriptionAction {
  return {
    type: ActionTypes.SetZoningDescription,
    value,
  }
}

export const actionCreators = {
  addComp,
  addLot,
  addSpace,
  addTransaction,
  editLot,
  editSpace,
  loadAddProperty,
  loadEditProperty,
  removeComp,
  removeLot,
  removeSpace,
  removeTransaction,
  resetState,
  save,
  setAddress,
  setAvgDailyRate,
  setAvgOccupancy,
  setBuildingClass,
  setBuildingSize,
  setCeilingHeight,
  setCorridor,
  setDockHighDoors,
  setDriveInDoors,
  setGrossLeasableArea,
  setGrossRentMultiplier,
  setHighlights,
  setInsuranceExpenses,
  setLatitude,
  setLongitude,
  setLocationDescription,
  setLotSize,
  setMainPhoto,
  setNumberOfUnits,
  setOccupancy,
  setOtherExpenses,
  setOwners,
  setParcelId,
  setPropertyDescription,
  setPropertyName,
  setPropertyTypes,
  setRealEstateTaxes,
  setRelatedConnections,
  setRevPerPAR,
  setSigns,
  setChatChannelId,
  setSocial,
  setYearBuilt,
  setZoningDescription,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<PropertyEditState> = (
  state: PropertyEditState = new PropertyEditState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction

  switch (action.type) {
    case ActionTypes.AddComp:
      return state.setIn(
        [
          'property',
          'comps',
          !state.property.comps.size || state.property.comps.size === 0
            ? 0
            : state.property.comps.size,
        ],
        action.value,
      ) as PropertyEditState

    case ActionTypes.AddLot:
      return state.setIn(
        [
          'property',
          'lotDetails',
          !state.property.lotDetails.size ||
          state.property.lotDetails.size === 0
            ? 0
            : state.property.lotDetails.size,
        ],
        action.value,
      ) as PropertyEditState

    case ActionTypes.AddSpace:
      return state.setIn(
        [
          'property',
          'spaceDetails',
          !state.property.spaceDetails.size ||
          state.property.spaceDetails.size === 0
            ? 0
            : state.property.spaceDetails.size,
        ],
        action.value,
      ) as PropertyEditState

    case ActionTypes.AddTransaction:
      if (action.mode === 'add') {
        return state.setIn(
          [
            'property',
            'transactionHistory',
            !state.property.transactionHistory.size ||
            state.property.transactionHistory.size === 0
              ? 0
              : state.property.transactionHistory.size,
          ],
          action.value,
        ) as PropertyEditState
      } else {
        const transactionIndex = state.property.transactionHistory.findIndex(
          (item: any) => {
            return item.id === action.value.id
          },
        )

        return state.setIn(
          ['property', 'transactionHistory', transactionIndex],
          action.value,
        ) as PropertyEditState
      }
    case ActionTypes.EditLot:
      const lotIndexToEdit = state.property.lotDetails.findIndex(
        (item: any) => {
          if (!item) {
            return false
          }
          return item.get('id') === action.value.id
        },
      )

      return state.setIn(
        ['property', 'lotDetails', lotIndexToEdit],
        action.value,
      ) as PropertyEditState

    case ActionTypes.EditSpace:
      const spaceIndexToEdit = state.property.spaceDetails.findIndex(
        (item: any) => {
          if (!item) {
            return false
          }
          return item.get('id') === action.value.id
        },
      )

      return state.setIn(
        ['property', 'spaceDetails', spaceIndexToEdit],
        action.value,
      ) as PropertyEditState

    case ActionTypes.LoadPropertyFailure:
      return state
        .set('error', action.error)
        .set('isLoading', false)
        .set('isReady', false) as PropertyEditState

    case ActionTypes.LoadPropertyRequest:
      return state
        .set('isLoading', true)
        .set('isReady', false) as PropertyEditState

    case ActionTypes.LoadPropertySuccess:
      return state
        .set('error', undefined)
        .set('property', action.value)
        .set('isLoading', false)
        .set('isReady', true)
        .set('canSave', true) as PropertyEditState

    case ActionTypes.RemoveComp:
      const compIndexToRemove = state.property.comps.findIndex(
        (item?: Comp) => {
          if (!item) {
            return false
          }
          return item.get('propertyId') === action.value.propertyId
        },
      )

      return state.removeIn([
        'property',
        'comps',
        compIndexToRemove,
      ]) as PropertyEditState

    case ActionTypes.RemoveLot:
      const lotIndexToRemove = state.property.lotDetails.findIndex(
        (item?: Lot) => {
          if (!item) {
            return false
          }
          return item.get('id') === action.value.id
        },
      )

      return state.removeIn([
        'property',
        'lotDetails',
        lotIndexToRemove,
      ]) as PropertyEditState

    case ActionTypes.RemoveSpace:
      const spaceIndexToRemove = state.property.spaceDetails.findIndex(
        (item?: Space) => {
          if (!item) {
            return false
          }
          return item.get('id') === action.value.id
        },
      )

      return state.removeIn([
        'property',
        'spaceDetails',
        spaceIndexToRemove,
      ]) as PropertyEditState

    case ActionTypes.RemoveTransaction:
      const transactionIndexToRemove = state.property.transactionHistory.findIndex(
        (item?: TransactionHistory) => {
          if (!item) {
            return false
          }
          return item.get('id') === action.value.id
        },
      )

      return state.removeIn([
        'property',
        'transactionHistory',
        transactionIndexToRemove,
      ]) as PropertyEditState

    case ActionTypes.ResetState:
      return new PropertyEditState()

    case ActionTypes.SavePropertyFailure:
      return state
        .set('error', action.error)
        .set('isSaving', false) as PropertyEditState

    case ActionTypes.SavePropertyRequest:
      return state.set('isSaving', true) as PropertyEditState

    case ActionTypes.SavePropertySuccess:
      return state
        .set('error', undefined)
        .set('isSaving', true) as PropertyEditState

    case ActionTypes.SetAddress:
      return state.setIn(
        ['property', 'address'],
        action.value,
      ) as PropertyEditState

    case ActionTypes.SetAvgDailyRate:
      return state.setIn(
        ['property', 'avgDailyRate'],
        action.value,
      ) as PropertyEditState

    case ActionTypes.SetAvgOccupancy:
      return state.setIn(
        ['property', 'avgOccupancy'],
        action.value,
      ) as PropertyEditState

    case ActionTypes.SetBuildingClass:
      return state.setIn(
        ['property', 'buildingClass'],
        action.value,
      ) as PropertyEditState

    case ActionTypes.SetBuildingSize:
      return state.setIn(
        ['property', 'totalBuildingSize'],
        action.value,
      ) as PropertyEditState

    case ActionTypes.SetCeilingHeight:
      return state.setIn(
        ['property', 'ceilingHeight'],
        action.value,
      ) as PropertyEditState

    case ActionTypes.SetCorridor:
      return state.setIn(
        ['property', 'corridor'],
        action.value,
      ) as PropertyEditState

    case ActionTypes.SetDockHighDoors:
      return state.setIn(
        ['property', 'noOfDockHighDoorsLoading'],
        action.value,
      ) as PropertyEditState

    case ActionTypes.SetDriveInDoors:
      return state.setIn(
        ['property', 'noOfDriveInGradeLevelDoors'],
        action.value,
      ) as PropertyEditState

    case ActionTypes.SetGrossLeasableArea:
      return state.setIn(
        ['property', 'grossLeaseableArea'],
        action.value,
      ) as PropertyEditState

    case ActionTypes.SetGrossRentMultiplier:
      return state.setIn(
        ['property', 'grossRentMultiplier'],
        action.value,
      ) as PropertyEditState

    case ActionTypes.SetHighlights:
      return state.setIn(
        ['property', 'highlights'],
        action.value,
      ) as PropertyEditState

    case ActionTypes.SetInsuranceExpenses:
      return state.setIn(
        ['property', 'financialData', 'insuranceExpenses'],
        action.value,
      ) as PropertyEditState

    case ActionTypes.SetLatitude:
      return state.setIn(
        ['property', 'gps', 'latitude'],
        action.value,
      ) as PropertyEditState

    case ActionTypes.SetLongitude:
      return state.setIn(
        ['property', 'gps', 'longitude'],
        action.value,
      ) as PropertyEditState

    case ActionTypes.SetLocationDescription:
      return state.setIn(
        ['property', 'locationDescription'],
        action.value,
      ) as PropertyEditState

    case ActionTypes.SetLotSize:
      return state.setIn(
        ['property', 'totalLotSize'],
        action.value,
      ) as PropertyEditState

    case ActionTypes.SetMainPhoto:
      return state.setIn(
        ['property', 'thumbnailImageId'],
        action.photoId,
      ) as PropertyEditState

    case ActionTypes.SetNumberOfUnits:
      return state.setIn(
        ['property', 'noOfUnits'],
        action.value,
      ) as PropertyEditState

    case ActionTypes.SetOccupancy:
      return state.setIn(
        ['property', 'occupancy'],
        action.value,
      ) as PropertyEditState

    case ActionTypes.SetOtherExpenses:
      return state.setIn(
        ['property', 'financialData', 'otherExpenses'],
        action.value,
      ) as PropertyEditState

    case ActionTypes.SetOwners:
      return state.setIn(
        ['property', 'owners'],
        action.value,
      ) as PropertyEditState

    case ActionTypes.SetParcelId:
      return state.setIn(
        ['property', 'parcelId'],
        action.value,
      ) as PropertyEditState

    case ActionTypes.SetPropertyDescription:
      return state.setIn(
        ['property', 'propertyDescription'],
        action.value,
      ) as PropertyEditState

    case ActionTypes.SetPropertyName:
      return state.setIn(
        ['property', 'name'],
        action.value,
      ) as PropertyEditState

    case ActionTypes.SetPropertyTypes:
      return state.setIn(
        ['property', 'propertyTypes'],
        action.value,
      ) as PropertyEditState

    case ActionTypes.SetRealEstateTaxes:
      return state.setIn(
        ['property', 'financialData', 'realEstateTaxes'],
        action.value,
      ) as PropertyEditState

    case ActionTypes.SetRelatedConnections:
      return state.setIn(
        ['property', 'associated'],
        action.value,
      ) as PropertyEditState

    case ActionTypes.SetRevPerPAR:
      return state.setIn(
        ['property', 'revPerPAR'],
        action.value,
      ) as PropertyEditState

    case ActionTypes.SetSigns:
      return state.setIn(
        ['property', 'signs'],
        action.value as List<Sign>,
      ) as PropertyEditState

    case ActionTypes.SetChatChannelId:
      return state.setIn(
        ['property', 'chatChannelId'],
        action.value as string,
      ) as PropertyEditState

    case ActionTypes.SetSocial:
      const social = state.getIn(['property', 'social']).find((item: any) => {
        return item.type === action.value.type
      })

      if (!social) {
        return state.setIn(
          ['property', 'social'],
          state.getIn(['property', 'social']).push(action.value),
        ) as PropertyEditState
      } else {
        const indexToUpdate = state
          .getIn(['property', 'social'])
          .findIndex((item: any) => {
            return item.get('type') === action.value.type
          })

        return state.setIn(
          ['property', 'social', indexToUpdate, 'username'],
          action.value.username,
        ) as PropertyEditState
      }

    case ActionTypes.SetYearBuilt:
      return state.setIn(
        ['property', 'yearBuilt'],
        action.value,
      ) as PropertyEditState

    case ActionTypes.SetZoningDescription:
      return state.setIn(
        ['property', 'zoningDescription'],
        action.value,
      ) as PropertyEditState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* loadAddPropertySaga(action: ILoadAddPropertyAction) {
  try {
    yield put(loadPropertyRequest())

    if (!action.siteId) {
      yield put(loadPropertySuccess(new Property()))
      return
    }

    const result = yield makeAuthenticatedRequest(
      `/api/DevelopmentClients/undefined/Sites/${action.siteId}`,
    )

    if (result.success === true) {
      const site = DevelopmentSite.fromJS(result.entity)

      const property = new Property({
        address: site.address,
        attachments: site.attachments,
        name: site.title,
        photos: site.photos,
        siteId: site.id,
        thumbnailImage: site.coverPhoto,
        thumbnailImageId: site.coverPhotoId,
      })

      yield put(loadPropertySuccess(property))
    } else {
      yield put(loadPropertyFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadPropertyFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* loadEditPropertySaga(action: ILoadEditPropertyAction) {
  try {
    yield put(loadPropertyRequest())

    const result = yield makeAuthenticatedRequest(
      `/api/Properties/${action.propertyId}`,
    )

    if (result.success === true) {
      yield put(loadPropertySuccess(Property.fromJS(result.entity)))
    } else {
      yield put(loadPropertyFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadPropertyFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* savePropertySaga(action: ISavePropertyAction) {
  try {
    yield put(saveRequest())

    const baseUrl = action.property.id
      ? `/api/Properties/${action.property.id}`
      : '/api/Properties'

    const photos: Map<string, FileUploadAttachment> = yield select(
      selectFiles(PHOTOS_IDENTIFIER),
    )
    const photosList = photos?.toArray().reduce((list, fileUpload) => {
      if (fileUpload === undefined || fileUpload.error) {
        return list
      }

      return list.push(fileUpload.attachment)
    }, List<Attachment>())

    const attachments: Map<string, FileUploadAttachment> = yield select(
      selectFiles(ATTACHMENTS_IDENTIFIER),
    )
    const attachmentsList = attachments
      ? attachments.toArray().reduce((list, fileUpload) => {
          if (fileUpload === undefined || fileUpload.error) {
            return list
          }

          return list.push(fileUpload.attachment)
        }, List<Attachment>())
      : List<Attachment>()

    const property = action.property
      .set('attachments', attachmentsList)
      .set('photos', photosList)
      .toJS()

    const propertyOpts = {
      body: JSON.stringify(property),
      method: action.property.id ? RequestMethod.PUT : RequestMethod.POST,
    }

    const result = yield makeAuthenticatedRequest(baseUrl, propertyOpts)

    if (result.success === true) {
      yield put(saveSuccess())
      yield put(resetState())
      yield put(
        successToast(
          'Success',
          action.property.id
            ? `${action.property.name}'s information updated`
            : 'New Property Created',
        ),
      )
      yield put(
        push(`/properties/property/${action.property.id || result.entity.id}`),
      )
    } else {
      yield put(saveFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(saveFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchLoadAddProperty() {
  yield takeLatest(ActionTypes.LoadAddProperty, loadAddPropertySaga)
}

function* watchLoadEditProperty() {
  yield takeLatest(ActionTypes.LoadEditProperty, loadEditPropertySaga)
}

function* watchSaveProperty() {
  yield takeLatest(ActionTypes.SaveProperty, savePropertySaga)
}

export function* propertyEditRootSaga() {
  yield fork(watchLoadAddProperty)
  yield fork(watchLoadEditProperty)
  yield fork(watchSaveProperty)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (state: ApplicationState): PropertyEditState =>
  state.propertyEdit

export const selectCanSave = () =>
  createSelector(selectModuleState(), (mState) => mState.canSave)

export const selectError = () =>
  createSelector(selectModuleState(), (mState) => mState.error)

export const selectIsLoading = () =>
  createSelector(selectModuleState(), (mState) => mState.isLoading)

export const selectIsReady = () =>
  createSelector(selectModuleState(), (mState) => mState.isReady)

export const selectIsSaving = () =>
  createSelector(selectModuleState(), (mState) => mState.isSaving)

export const selectProperty = () =>
  createSelector(selectModuleState(), (mState) => mState.property)
